import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import {
  Campaign,
  CampaignInventory,
  Character,
  CharacterCustomItem,
  CharacterInventory,
  EquipmentPack,
  Item,
  MagicItemKeyword,
  PseudoItem,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import CharacterManageInventoryItem from "@dndComponents/character/inventory/manage/CharacterManageInventoryItem";
import FilterBox from "@dndComponents/filtering/FilterBox";
import CharacterItemDetailsSidebar from "@dndComponents/character/inventory/item/CharacterItemDetailsSidebar";
import CommonUtilities from "@utilities/common_utilities";
import PageLinks from "@components/Navigation/PageLinks";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import LoadingBox from '@components/input/LoadingBox';

interface Props {
  obj: Character | Campaign;
  char?: Character;
  inventory: CharacterInventory | CampaignInventory;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterManageInventory({
  obj,
  char,
  inventory,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [expanded, setExpanded] = useState("");
  const [equipment_packs, setEquipmentPacks] = useState<EquipmentPack[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [true_filter, setTrueFilter] = useState<any>({ });
  const [options, setOptions] = useState<(Item | EquipmentPack)[]>([]);
  const [proficient, setProficient] = useState<boolean | null>(null);
  // const [max_rarity, setMaxRarity] = useState<number | null>(null);
  const [adding_custom_item, setAddingCustomItem] = useState<CharacterCustomItem | null>(null);
  const [page_num, setPageNum] = useState(0);
  
  const load = useCallback( async () => {
    const new_equipment_packs = data_util.equipment_packs;
    const new_items = data_util.items.filter(o => o.min_rarity !== -1);
    setEquipmentPacks(new_equipment_packs);
    setItems(new_items);
    setReloading(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      calc_use_list();
    }
    // eslint-disable-next-line
  }, [reloading]);

  const updateFilter = (changed: any) => {
    const old_filter = {...true_filter};
    let is_changed = false;
    const the_filter = {...changed};
    if (the_filter.effect_type) {
      the_filter.the_effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.the_attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    the_filter.vehicle = { "$in": [undefined, false] };
    if (the_filter.proficient === undefined && proficient !== null) {
      setProficient(null);
      is_changed = true;
    } else if (proficient !== the_filter.proficient && (proficient !== null || the_filter.proficient !== undefined)) {
      setProficient(the_filter.proficient);
      is_changed = true;
    }
    delete the_filter.proficient;
    if (!is_changed) {
      is_changed = !common_util.recursive_compare_objects(old_filter, the_filter);
    }
    if (is_changed) {
      setPageNum(0);
      setTrueFilter(the_filter);
      setReloading(true);
    }
  }

  const calc_use_list = () => {
    const the_filter = {...true_filter};
    let filtered_items = api.filter("", items, the_filter) as Item[];
    const ids = filtered_items.map(o => o._id);
    const filtered_miks = (api.filter("", data_util.magic_item_keywords, the_filter) as MagicItemKeyword[]).filter(o => o.min_rarity !== -1);
    // if (max_rarity !== null) {
    //   filtered_items = filtered_items.filter(o => o.min_rarity <= max_rarity);
    //   filtered_miks = filtered_miks.filter(o => o.min_rarity <= max_rarity);
    // }
    const other_items = items.filter(o => !ids.includes(o._id) &&
      filtered_miks.find(o2 => o2.possible_base_item_ids.includes(o._id)));
    filtered_items = [...filtered_items,...other_items];
    if (proficient !== null && char) {
      filtered_items = filtered_items.filter(o => proficient === char_util.proficient(char, o));
    }
    const filtered_equipment_packs = api.filter("", equipment_packs, the_filter) as EquipmentPack[];
    const options = [...filtered_items, ...filtered_equipment_packs];
    setOptions(options);
  }

  const renderItems = () => {
    const page_size = 10;
    console.log(options);
    const sorted = options.sort((a, b) => {
      if (a instanceof Item && b instanceof Item) {
        if (a.min_rarity !== b.min_rarity) {
          return a.min_rarity - b.min_rarity;
        }
      }
      return a.name.localeCompare(b.name);
    });
    const page_count = Math.ceil(options.length / page_size);
    const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
    return (
      <Grid size={12}
        container spacing={0} direction="row">
        { filtered_and_paged.map(item => {
          return renderItem(item);
        })}
        <Grid size={12}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
      </Grid>
    );
  }

  const renderItem = (item: Item | EquipmentPack) => {
    let is_expanded = false;
    if (item instanceof Item) {
      is_expanded = expanded === item.id;
    } else {
      is_expanded = expanded === item._id;
    }
    // if (item instanceof PseudoItem && item.name.includes("Amethyst")) {
    //   debugger;
    // }
    return (
      <Grid size={12} key={item._id} className="padded">
        { !reloading &&
          <CharacterManageInventoryItem
            char={obj}
            inventory={inventory}
            item={item}
            expanded={is_expanded}
            all_base_items={items}
            // log_it={item instanceof PseudoItem && item.name.includes("Amethyst")}
            onExpand={() => {
              if (is_expanded) {
                setExpanded("");
              } else if (item instanceof PseudoItem) {
                setExpanded(item.id);
              } else {
                setExpanded(item._id);
              }
            }}
          />
        }
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else if (adding_custom_item) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterItemDetailsSidebar
            char={obj}
            char_item={adding_custom_item}
            shared={shared}
            onRemove={() => {
              debugger;
              console.error("onRemove Not Implemented");
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          Done
        </Grid>
      </Grid>
    );
  } else {
    console.log(items);
    console.log(options);
    return (
      <div>
        <Grid container spacing={1} direction="column">
          <Grid className="header centered">
            Manage Inventory
          </Grid>
          <Grid>
            <Grid container spacing={0} direction="row">
              <Grid size={12}>
                <DandDButtonBox
                  name="Add Custom Item"
                  onClick={() => {
                    const custom_item = new CharacterCustomItem();
                    custom_item.name = "Custom Item";
                    connect_util.addItem({ char: obj, item: custom_item }).then(char_item => {
                      obj.changed();
                      obj.subscription_holder.itemsChanged();
                      if (char_item instanceof CharacterCustomItem) {
                        setAddingCustomItem(char_item);
                      }
                    });
                  }}
                />
              </Grid>
              <Grid size={12}>
                <FilterBox
                  data_type="item"
                  extra_fields={["proficient"]}
                  quick_fields={["item_type","rarities","damage_type","attunement","proficient"]}
                  flat
                  dont_use_api
                  all_objects={items}
                  c_options={obj.c_options}
                  the_filter={{ search_string: [""] }}
                  onChange={updateFilter}
                />
              </Grid>
              { renderItems() }
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  }
}